import { useAsyncFn } from 'react-use';
import { useParams } from 'react-router-dom';
import { ProjectFilterType, ProjectGenreFilterType, ProjectsResponseModel } from '@uniquegood/realworld-admin-interface';
import { useEffect, useRef } from 'react';
import { applicationProjectApi, projectApi } from '@src/apis/admin';

interface UseProjectListParams {
  currentPage: number;
  searchKeyword: string;
  filter: ProjectFilterType[];
  genreFilter: ProjectGenreFilterType[];
  isCreatorProject: boolean;
}

const PAGE_SIZE = 15;
const projectFilterByKeyword = (keyword: string, project: ProjectsResponseModel) => {
  const normalizedKeyword = keyword.trim().toLowerCase();

  if (!normalizedKeyword) return true;

  const projectId = String(project.id ?? '').toLowerCase();
  const projectName = String(project.name ?? '').toLowerCase();
  const projectDescription = String(project.description ?? '').toLowerCase();

  return (
    projectId === normalizedKeyword ||
    projectName.includes(normalizedKeyword) ||
    projectDescription.includes(normalizedKeyword)
  );
};

export default function useProjectList({
  currentPage,
  searchKeyword,
  filter,
  genreFilter,
  isCreatorProject
}: UseProjectListParams) {
  const { id } = useParams();
  const projectCache = useRef<{ cacheKey: string; allProjects: ProjectsResponseModel[] }>({
    cacheKey: '',
    allProjects: []
  });

  const [projectList, fetchProjectList] = useAsyncFn(async () => {
    try {
      const normalizedSearchKeyword = searchKeyword.trim().toLowerCase();

      if (!normalizedSearchKeyword) {
        projectCache.current = {
          cacheKey: '',
          allProjects: []
        };

        if (isCreatorProject) {
          const { data } = await applicationProjectApi.getApplicationProjects(id || '', undefined, currentPage, PAGE_SIZE);

          return data.data;
        }

        const { data } = await projectApi.getProjects(undefined, filter, genreFilter, currentPage, PAGE_SIZE);

        return data.data;
      }

      const projectCacheKey = JSON.stringify({
        id: id || '',
        filter: [...filter].sort(),
        genreFilter: [...genreFilter].sort(),
        isCreatorProject
      });

      if (projectCache.current.cacheKey !== projectCacheKey) {
        let totalProjectCount = 0;

        if (isCreatorProject) {
          const { data } = await applicationProjectApi.getApplicationProjects(id || '', undefined, 1, 1);

          totalProjectCount = data.data.count || 0;

          if (totalProjectCount === 0) {
            projectCache.current = {
              cacheKey: projectCacheKey,
              allProjects: []
            };

            return {
              data: [],
              count: 0
            };
          }

          const allProjectsResponse = await applicationProjectApi.getApplicationProjects(
            id || '',
            undefined,
            1,
            totalProjectCount
          );

          projectCache.current = {
            cacheKey: projectCacheKey,
            allProjects: allProjectsResponse.data.data.data || []
          };
        } else {
          const { data } = await projectApi.getProjects(undefined, filter, genreFilter, 1, 1);

          totalProjectCount = data.data.count || 0;

          if (totalProjectCount === 0) {
            projectCache.current = {
              cacheKey: projectCacheKey,
              allProjects: []
            };

            return {
              data: [],
              count: 0
            };
          }

          const allProjectsResponse = await projectApi.getProjects(undefined, filter, genreFilter, 1, totalProjectCount);

          projectCache.current = {
            cacheKey: projectCacheKey,
            allProjects: allProjectsResponse.data.data.data || []
          };
        }
      }

      const startIndex = (currentPage - 1) * PAGE_SIZE;
      const endIndex = startIndex + PAGE_SIZE;
      const matchedProjects = projectCache.current.allProjects.filter((project) =>
        projectFilterByKeyword(normalizedSearchKeyword, project)
      );

      return {
        data: matchedProjects.slice(startIndex, endIndex),
        count: matchedProjects.length
      };
    } catch (e) {
      console.error(e);
      return undefined;
    }
  }, [currentPage, searchKeyword, filter, genreFilter, id, isCreatorProject]);

  useEffect(() => {
    fetchProjectList();
  }, [fetchProjectList]);

  return [projectList, fetchProjectList] as const;
}
